import type { PoiWindow } from "@trip/domain/poi";
import { Heart, Plus, Trash2, X } from "lucide-react";
import { useId, useState } from "react";
import { COLOUR_NAMES, UNCATEGORISED, categoryName } from "../lib/category-icons.ts";
import type { Poi, PoiCategory } from "../lib/pois.ts";
import { ConfirmDialog } from "../ui/index.ts";
import { TAKES_ALONG, failure } from "./PoiView.tsx";

/**
 * What a save sends: the name, the one category (null for none), and the windows, whole, in the
 * order shown.
 */
export type PoiDraft = {
  name: string;
  categoryId: string | null;
  /** Trimmed; null when empty, never an empty string. */
  note: string | null;
  windows: PoiWindow[];
};

/** Sunday first, as the API numbers them: 0 is Sunday, 6 is Saturday. */
const DAYS = [
  ["Sun", "Sunday"],
  ["Mon", "Monday"],
  ["Tue", "Tuesday"],
  ["Wed", "Wednesday"],
  ["Thu", "Thursday"],
  ["Fri", "Friday"],
  ["Sat", "Saturday"],
] as const;

/**
 * The latest a window may close when it runs past midnight. 22 to 2 is a night out; 18 to 17 is a
 * typo for 17 to 18, and taking it at its word would store a place open twenty-three hours a day.
 * Nothing on a trip opens in the evening and closes the next afternoon, so a crossing window has
 * to end in the small hours, and anything later is refused with the reason and the way round it:
 * two windows, either side of midnight.
 */
export const CROSSING_LATEST_END = 6;

/** The API's limit on windows per place. */
const MAX_WINDOWS = 20;

const at = (h: number) => `${String(h).padStart(2, "0")}:00`;

/** One window as the form holds it: no days picked is any day; no opening hour is any time. */
type Row = { key: number; days: number[]; start: number | null; end: number | null };

let nextKey = 0;
const row = (w: PoiWindow): Row => ({
  key: ++nextKey,
  days: w.days ?? [],
  start: w.startHour,
  end: w.startHour === null ? null : w.endHour,
});

/**
 * What a window's hours mean, in words, or why they are refused. Start and end come as a pair or
 * not at all (the form keeps them so); `start > end` runs past midnight and is legal only into the
 * small hours; the same hour twice is the whole day, as the score reads it, and is said so.
 */
function hoursSay(r: Row): { text: string; problem: boolean } {
  const { start, end } = r;
  if (start === null || end === null) return { text: "At any time of day.", problem: false };
  if (start === end)
    return {
      text: `Opens and closes at ${at(start)}: that is all day, round the clock.`,
      problem: false,
    };
  if (start < end) return { text: `${at(start)} to ${at(end)}.`, problem: false };
  if (end <= CROSSING_LATEST_END)
    return {
      text: `Runs past midnight: ${at(start)} to ${at(end)} the next morning.`,
      problem: false,
    };
  return {
    text: `Closes at ${at(end)}, before it opens at ${at(start)}. A window can run past midnight only into the small hours, closing by ${at(CROSSING_LATEST_END)}. If it really is open that long, enter it as two windows: one until midnight (${at(start)} to 24:00), and one from midnight (00:00 to ${at(end)}) on the days after.`,
    problem: true,
  };
}

function daysSay(days: number[]): string {
  if (days.length === 0 || days.length === 7) return "Any day.";
  return `${[...days]
    .sort((a, b) => a - b)
    .map((d) => DAYS[d]![0])
    .join(", ")}.`;
}

/** Who likes it, the reader first, since the count alone cannot say whether they are in it. */
function votesSay(votes: number, voted: boolean): string {
  const others = Math.max(0, votes - (voted ? 1 : 0));
  if (voted && others === 0) return "You like it.";
  if (voted) return `You and ${others} ${others === 1 ? "other" : "others"} like it.`;
  if (votes === 0) return "Nobody has liked it yet.";
  return `${votes} ${votes === 1 ? "person likes" : "people like"} it.`;
}

/** The list is the source of truth for what it brought: sync puts it back. */
const COMES_BACK =
  "It came from the Google list and returns on the next sync unless it is removed there.";

/**
 * Layer 2's editor: what one place is, typed by the people going. Laid out as the category form
 * is, so the two are one thing to learn. Top to bottom: a live pin (the face and colour of the
 * category chosen, as the map will draw it), the name, the category as faces (a new one for the
 * trip, or none, which shows it under "All" only), the list's note, who likes it, the days and
 * hours it is open, the actions, and, ruled off at the bottom and only where `onRemove` is given,
 * Delete with what it takes along. With `poi` null it is a new place, which also needs a name;
 * where it goes is the page's to say, not the form's, and moving it later is dragging its pin.
 *
 * Nothing is shown saved before the server says so. `onSave` resolves once the server has the
 * place and the page then closes the editor; it rejects when it does not, and the editor stays
 * open with everything as typed and the reason on it. A vote is its own write, sent at once, and
 * shown when the page hands back the place as the server now has it. Deleting asks first, then
 * waits for the server the same way.
 */
export function PoiEditor({
  poi,
  initial,
  categories,
  onSave,
  onAddCategory,
  onVote,
  onRemove,
  onClose,
  where,
}: {
  /** The place as the server last sent it, or null for a new one. */
  poi: Poi | null;
  /**
   * What a new place starts with where something has already found it out: the name and hours of a
   * place Google knows, so the member only picks a category and adds a note. Read once, as `poi`
   * is, and ignored for a place that exists, which carries its own.
   */
  initial?: { name: string; windows: PoiWindow[] } | undefined;
  categories: PoiCategory[];
  onSave: (draft: PoiDraft) => Promise<void>;
  /** Resolves with the category the trip now has under that name, new or not. */
  onAddCategory: (name: string) => Promise<PoiCategory>;
  /** A vote, or taking it back. Absent for a new place, which nobody can want yet. */
  onVote?: ((on: boolean) => Promise<void>) | undefined;
  /**
   * Deletes the place; resolves once the server has, rejects when it has not. Given only to a
   * reader the server would let (whoever added the place, or a trip admin), and only for a place
   * that exists: where it is absent there is no Delete at all, not a disabled one.
   */
  onRemove?: (() => Promise<void>) | undefined;
  onClose: () => void;
  /** Where a new place will go, in words. */
  where?: string | undefined;
}) {
  const [name, setName] = useState(poi?.name ?? initial?.name ?? "");
  // A place keeps what it has, none included; a new one starts in the trip's first category.
  const [note, setNote] = useState(poi?.note ?? "");
  const [categoryId, setCategoryId] = useState<string | null>(
    poi ? poi.categoryId : (categories[0]?.id ?? null),
  );
  const [rows, setRows] = useState<Row[]>(() => {
    if (poi) return (Array.isArray(poi.windows) ? poi.windows : []).map(row);
    return (Array.isArray(initial?.windows) ? initial.windows : []).map(row);
  });
  /** Categories added here and not yet in the page's list, so a new one is a choice at once. */
  const [added, setAdded] = useState<PoiCategory[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [busy, setBusy] = useState<"save" | "category" | "vote" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const categoryLabel = useId();

  const choices = [...categories, ...added.filter((a) => !categories.some((c) => c.id === a.id))];
  // The pin as the map draws it: the chosen category's face and colour, or the plain grey one.
  const pin = choices.find((c) => c.id === categoryId) ?? UNCATEGORISED;
  const colourName = COLOUR_NAMES[pin.color as keyof typeof COLOUR_NAMES] ?? "Custom";
  const said = rows.map(hoursSay);
  const invalid = said.some((s) => s.problem);
  const missing = name.trim() === "" ? "Give the place a name." : null;

  const edit = (key: number, change: (r: Row) => Row) =>
    setRows((rs) => rs.map((r) => (r.key === key ? change(r) : r)));

  async function run(
    what: "save" | "category" | "vote" | "delete",
    act: () => Promise<void>,
    failed: string,
  ) {
    setBusy(what);
    setError(null);
    try {
      await act();
    } catch (e) {
      console.error("places: an edit did not reach the server", e);
      setError(`${failed} ${failure(e)}`);
    } finally {
      setBusy(null);
    }
  }

  const save = () =>
    run(
      "save",
      () =>
        onSave({
          name: name.trim(),
          categoryId,
          note: note.trim() === "" ? null : note.trim(),
          windows: rows.map((r) => ({
            days: r.days.length === 0 ? null : [...r.days].sort((a, b) => a - b),
            startHour: r.start,
            endHour: r.start === null ? null : r.end,
          })),
        }),
      "Not saved.",
    );

  const addCategory = () =>
    run(
      "category",
      async () => {
        const c = await onAddCategory(newCategory.trim());
        setAdded((a) => (a.some((x) => x.id === c.id) ? a : [...a, c]));
        setCategoryId(c.id);
        setNewCategory("");
      },
      "Category not added.",
    );

  const title = poi ? `Edit ${poi.name}` : "New place";
  return (
    <section className="poi-editor" aria-label={title}>
      <header className="poi-editor-head">
        <h2 className="poi-editor-title">{poi ? poi.name : "New place"}</h2>
        <button
          type="button"
          className="icon-button poi-editor-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </header>
      <div className="poi-editor-body">
        {!poi && where && <p className="small muted">{where}</p>}

        <span
          className="cat-preview"
          role="img"
          aria-label={`Preview: ${pin.emoji} in ${colourName.toLowerCase()}`}
          style={{ background: pin.color }}
        >
          {pin.emoji}
        </span>

        <label className="poi-editor-field">
          <span className="label">Name</span>
          <input
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
          />
        </label>

        <div className="poi-editor-field">
          <span className="label" id={categoryLabel}>
            Category
          </span>
          <div role="group" aria-labelledby={categoryLabel} className="poi-editor-categories">
            {choices.map((c) => (
              <button
                key={c.id}
                type="button"
                className="poi-editor-category"
                aria-label={c.name}
                aria-pressed={categoryId === c.id}
                onClick={() => setCategoryId(c.id)}
              >
                <span
                  className="poi-editor-category-face"
                  style={{ background: c.color }}
                  aria-hidden="true"
                >
                  {c.emoji}
                </span>
                <span aria-hidden="true">{categoryName(c.name)}</span>
              </button>
            ))}
            <button
              type="button"
              className="poi-editor-category"
              aria-label="No category"
              aria-pressed={categoryId === null}
              onClick={() => setCategoryId(null)}
            >
              <span
                className="poi-editor-category-face"
                style={{ background: UNCATEGORISED.color }}
                aria-hidden="true"
              >
                {UNCATEGORISED.emoji}
              </span>
              <span aria-hidden="true">No category</span>
            </button>
          </div>
          <div className="poi-editor-new-category">
            <input
              aria-label="New category"
              placeholder="Or add a new one"
              value={newCategory}
              maxLength={40}
              onChange={(e) => setNewCategory(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              className="button small"
              aria-label="Add category"
              disabled={newCategory.trim() === "" || busy !== null}
              onClick={() => void addCategory()}
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              Add
            </button>
          </div>
        </div>

        {/* The list's own comment, and the whole name when the list's was too long to keep. */}
        <label className="poi-editor-field">
          <span className="label">Note</span>
          <textarea
            rows={3}
            maxLength={4000}
            value={note}
            placeholder="Queue before 19:00. Ask for the black garlic one."
            onChange={(e) => setNote(e.target.value)}
          />
          {/* A fact about the name, not a guess about the note: the server shortens with an ellipsis. */}
          {poi?.name.endsWith("…") && (
            <span className="small muted">
              This place's full name was too long to fit, so it is kept at the start of the note.
            </span>
          )}
        </label>

        {poi && onVote && (
          <div className="poi-editor-votes">
            <p role="status" aria-label="Votes" className="small">
              {votesSay(poi.votes, poi.voted)}
            </p>
            <button
              type="button"
              className="poi-vote poi-vote-text"
              aria-pressed={poi.voted}
              disabled={busy === "vote"}
              onClick={() => void run("vote", () => onVote(!poi.voted), "Like not saved.")}
            >
              <Heart size={18} strokeWidth={1.75} aria-hidden="true" />
              Like
            </button>
          </div>
        )}

        <fieldset className="poi-editor-field">
          <legend className="label">Opening hours</legend>
          {rows.length === 0 && <p className="small">Open any day, at any time.</p>}
          {rows.map((r, i) => {
            const s = said[i]!;
            return (
              <div
                key={r.key}
                role="group"
                aria-label={`Opening hours ${i + 1}`}
                className="poi-window"
              >
                <div className="poi-days">
                  {DAYS.map(([short, long], d) => (
                    <button
                      key={d}
                      type="button"
                      aria-label={long}
                      aria-pressed={r.days.includes(d)}
                      onClick={() =>
                        edit(r.key, (x) => ({
                          ...x,
                          days: x.days.includes(d) ? x.days.filter((y) => y !== d) : [...x.days, d],
                        }))
                      }
                    >
                      {short}
                    </button>
                  ))}
                </div>
                <div className="poi-hours">
                  <label>
                    <span className="small">Opens</span>
                    <select
                      value={r.start ?? ""}
                      onChange={(e) => {
                        const v = e.target.value === "" ? null : Number(e.target.value);
                        edit(r.key, (x) => ({
                          ...x,
                          start: v,
                          // A pair or nothing: an opening hour brings a closing one, an hour on.
                          end: v === null ? null : (x.end ?? v + 1),
                        }));
                      }}
                    >
                      <option value="">Any time</option>
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>
                          {at(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="small">Closes</span>
                    <select
                      value={r.end ?? ""}
                      disabled={r.start === null}
                      onChange={(e) => edit(r.key, (x) => ({ ...x, end: Number(e.target.value) }))}
                    >
                      {r.start === null && <option value="">—</option>}
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h + 1} value={h + 1}>
                          {at(h + 1)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="icon-button poi-window-remove"
                    aria-label="Remove these hours"
                    onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                  >
                    <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                </div>
                <p className="small">{daysSay(r.days)}</p>
                {s.problem ? (
                  <p role="alert" className="error small">
                    {s.text}
                  </p>
                ) : (
                  <p className="small">{s.text}</p>
                )}
              </div>
            );
          })}
          {rows.length < MAX_WINDOWS && (
            <button
              type="button"
              className="button small poi-editor-add-window"
              onClick={() =>
                setRows((rs) => [...rs, row({ days: null, startHour: null, endHour: null })])
              }
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              Add opening hours
            </button>
          )}
        </fieldset>

        {error && (
          <p role="alert" className="error small">
            {error}
          </p>
        )}
        {missing && !invalid && <p className="small muted">{missing}</p>}

        <div className="cat-actions">
          <button type="button" className="button" disabled={busy !== null} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button primary"
            aria-busy={busy === "save" || undefined}
            disabled={busy !== null || invalid || missing !== null}
            onClick={() => void save()}
          >
            Save
          </button>
        </div>

        {poi && onRemove && (
          <div className="cat-remove">
            <p className="small muted">{TAKES_ALONG}</p>
            {poi.source === "IMPORT" && <p className="small muted">{COMES_BACK}</p>}
            <button
              type="button"
              className="button danger-outline"
              disabled={busy !== null}
              onClick={() => setAsking(true)}
            >
              Delete {poi.name}
            </button>
          </div>
        )}
      </div>
      {poi && onRemove && (
        <ConfirmDialog
          open={asking}
          title={`Delete ${poi.name}?`}
          body={<p>{TAKES_ALONG}</p>}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            setAsking(false);
            void run("delete", onRemove, "Not deleted.");
          }}
          onCancel={() => setAsking(false)}
        />
      )}
    </section>
  );
}
